import React, { useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { getCurrentUserDomainName } from '../../../utils/domainUtils';
import { sendFlashCheckRequest } from '../../../services/emailService';
import { 
  consumeCompanyCredits, 
  refundCompanyCredits
} from '../../../services/creditService';
import { FLASHCHECK_COST } from '../../../constants/pricing';
import { 
  ContentPadding, 
  FormContainer, 
  InputGroup, 
  Label, 
  Input, 
  TextArea, 
  SubmitButton, 
  SuccessMessage, 
  ErrorMessage 
} from './styled';

interface RequestTabProps {
  loading?: boolean;
  error?: string | null;
}

export const RequestTab: React.FC<RequestTabProps> = ({ loading, error }) => {
  const { currentUser, userData, companyData, refreshCompanyData } = useAuth();
  const [domain, setDomain] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Get current credits from company data (already loaded by AuthContext)
  const currentCredits = companyData?.creditBalance || 0;
  const companyId = companyData?.id;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!domain.trim()) {
      setSubmitError('Please provide a domain');
      return;
    }

    if (domain.trim().length < 3) {
      setSubmitError('Domain must be at least 3 characters long');
      return;
    }

    // Basic domain validation
    const domainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-]{1,61}[a-zA-Z0-9]\.[a-zA-Z]{2,}$/;
    if (!domainRegex.test(domain.trim())) {
      setSubmitError('Please enter a valid domain (e.g., example.com)');
      return;
    }

    // Check if user has sufficient credits
    if (!companyId || currentCredits < FLASHCHECK_COST) {
      setSubmitError(`Insufficient credits. You need ${FLASHCHECK_COST} credit to submit a FlashCheck request. Current balance: ${currentCredits} credits.`);
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    let creditDeductionId: string | null = null;
    let creditsDeducted = false;

    try {
      // Generate unique request ID for tracking
      creditDeductionId = `flashcheck_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      // Step 1: Deduct credits first (safer to deduct then refund if needed)
      await consumeCompanyCredits(
        companyId,
        FLASHCHECK_COST,
        currentUser?.uid || 'unknown',
        'flashcheck',
        creditDeductionId
      );

      creditsDeducted = true;
      
      // Credit deduction successful - will refresh after successful email send or during refund if needed

      // Step 2: Attempt to send FlashCheck request via email service
      const emailData = {
        customerEmail: userData?.email || 'Unknown',
        customerDomain: getCurrentUserDomainName(),
        requestedDomain: domain.trim(),
        message: message.trim(),
        timestamp: new Date().toISOString()
      };

      await sendFlashCheckRequest(emailData);
      
      // Step 3: If we reach here, everything succeeded
      // Refresh company data to immediately reflect updated credit balance
      await refreshCompanyData();
      
      setSubmitSuccess(true);
      setDomain('');
      setMessage('');
      
    } catch (err: any) {
      console.error('FlashCheck request failed:', err);
      
      // Step 4: Handle different failure scenarios with appropriate refund logic
      if (err.message && err.message.includes('Insufficient credits')) {
        // This shouldn't happen due to pre-validation, but handle it
        setSubmitError('Insufficient credits for FlashCheck request.');
      } else if (creditsDeducted && creditDeductionId) {
        // CRITICAL: Credits were deducted but email/service failed
        // Attempt automatic refund to maintain user trust and data integrity
        try {
          const refundResult = await refundCompanyCredits(
            companyId,
            FLASHCHECK_COST,
            currentUser?.uid || 'unknown',
            'flashcheck',
            creditDeductionId,
            'Email service failure'
          );
          
          if (refundResult.success) {
            // Refund successful - refresh company data to reflect updated balance
            await refreshCompanyData();
            setSubmitError('FlashCheck request failed to send, but your credit has been refunded. Please try again or contact support if the problem persists.');
          } else {
            // Refund failed - critical error requiring manual intervention
            setSubmitError('FlashCheck request failed and credit refund also failed. Please contact support immediately with this error message.');
          }
        } catch (refundError) {
          console.error('Refund failed:', refundError);
          setSubmitError('FlashCheck request failed and we were unable to refund your credit. Please contact support immediately.');
        }
      } else {
        // Credits were not deducted, so just show regular error
        setSubmitError('Failed to process FlashCheck request. Please try again or contact support if the problem persists.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ContentPadding>
      <FormContainer>
        <h3>Request FlashCheck Analysis</h3>
        <p>Submit a FlashCheck analysis request for a specific domain. This service costs {FLASHCHECK_COST} credit per request.</p>
        
        {/* Credit Information */}
        <div style={{ 
          background: 'rgba(99, 102, 241, 0.1)', 
          border: '1px solid rgba(99, 102, 241, 0.3)', 
          borderRadius: '8px', 
          padding: '12px 16px', 
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <span style={{ fontSize: '18px' }}>💎</span>
          <div>
            <strong>Current Balance: </strong>
            <span style={{ color: currentCredits >= FLASHCHECK_COST ? '#10b981' : '#ef4444' }}>
              {currentCredits} credits
            </span>
            <div style={{ fontSize: '0.85rem', color: '#64748b', marginTop: '2px' }}>
              Cost per FlashCheck request: {FLASHCHECK_COST} credit
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <InputGroup>
            <Label htmlFor="domain">Domain for FlashCheck *</Label>
            <Input
              id="domain"
              type="text"
              value={domain}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDomain(e.target.value)}
              placeholder="e.g., example.com"
              disabled={isSubmitting}
              required
            />
          </InputGroup>

          <InputGroup>
            <Label htmlFor="message">Additional Information (Optional)</Label>
            <TextArea
              id="message"
              value={message}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setMessage(e.target.value)}
              placeholder="Tell us more about your FlashCheck needs..."
              disabled={isSubmitting}
              rows={4}
              maxLength={500}
            />
            <div style={{ fontSize: '12px', color: '#666', textAlign: 'right', marginTop: '4px' }}>
              {message.length}/500 characters
            </div>
          </InputGroup>

          <SubmitButton 
            type="submit" 
            disabled={
              isSubmitting || 
              !domain.trim() || 
              !companyData ||
              currentCredits < FLASHCHECK_COST
            }
            style={{
              opacity: (
                isSubmitting || 
                !domain.trim() || 
                !companyData ||
                currentCredits < FLASHCHECK_COST
              ) ? 0.6 : 1,
              cursor: (
                isSubmitting || 
                !domain.trim() || 
                !companyData ||
                currentCredits < FLASHCHECK_COST
              ) ? 'not-allowed' : 'pointer'
            }}
          >
            {isSubmitting ? 'Processing...' : 
             !companyData ? 'Loading...' : 
             currentCredits < FLASHCHECK_COST ? 'Insufficient Credits' : 
             `Submit Request (${FLASHCHECK_COST} credit)`}
          </SubmitButton>
        </form>

        {submitSuccess && (
          <SuccessMessage variant="success" style={{ position: 'relative' }}>
            <button 
              onClick={() => setSubmitSuccess(false)}
              style={{
                position: 'absolute',
                top: '8px',
                right: '8px',
                background: 'none',
                border: 'none',
                fontSize: '18px',
                cursor: 'pointer',
                color: 'inherit',
                opacity: 0.7,
                lineHeight: 1
              }}
              title="Dismiss"
            >
              ×
            </button>
            ✅ FlashCheck request submitted successfully! {FLASHCHECK_COST} credit has been deducted from your account. We'll process your request and get back to you soon.
            <br />
            <small style={{ fontSize: '0.85rem', opacity: 0.9 }}>
              💡 Protected by automatic refund: If our system fails to process your request, your credit will be automatically refunded.
            </small>
          </SuccessMessage>
        )}

        {(submitError || error) && (
          <ErrorMessage variant="error">
            ❌ {submitError || error}
          </ErrorMessage>
        )}
      </FormContainer>
    </ContentPadding>
  );
};
